/**
 * 汎用サービス
 */
import { Injectable } from '@angular/core';
import { SupportClass } from '../../components/support-class';
import { AswServiceStoreService } from '../asw-service-store/asw-service-store.service';
import { AswCommonStoreService } from '../asw-common-store/asw-common-store.service';
import { AswContextStoreService } from '../asw-context-store/asw-context-store.service';
import { AMCMemberStoreService } from '../amc-member-store/amc-member-store.service';
import { AlertMessageStoreService } from '../alert-message-store/alert-message-store.service';
import { ErrorsHandlerService } from '../errors-handler/errors-handler.service';
import { NotificationStoreService } from '../notification-store/notification-store.service';
import { ApiErrorResponseService } from '../api-error-response/api-error-response.service';
import { AswMasterService } from '../asw-master/asw-master.service';
import { AnaBizContextStoreService } from '../ana-biz-context-store/ana-biz-context-store.service';
import { DynamicContentService } from '../dynamic-content/dynamic-content.service';
import { ApiErrorResponseModel, LoginStatusType } from '../../interfaces';
import { LoggerDatadogService } from '../logger-datadog/logger-datadog.service';
import { ConfidentialStoreService } from '../confidential-store/confidential-store.service';

/**
 * 汎用サービス
 *
 * ユーザー共通情報などよく使うサービスをひとまとまりにしたサービス
 *
 * 以下のサービスを内部に持つ　BaseComponentを使用したコンポーネントで以下のサービスを直接使おうとすると循環参照になって動かないのでCommonLibServiceを使うこと
 * @param alertMessageStoreService @see AlertMessageStoreService
 * @param apiErrorResponseService @see ApiErrorResponseService,
 * @param aswContextStoreService @see AswContextStoreService
 * @param aswCommonStoreService @see AswCommonStoreService
 * @param aswServiceStoreService @see AswServiceStoreService
 * @param amcMemberStoreService @see AMCMemberStoreService
 * @param anaBizContextStoreService @see AnaBizContextStoreService
 * @param dynamicContentService @see DynamicContentService
 * @param errorsHandlerService @see ErrorsHandlerService
 * @param notificationStoreService: @see NotificationStoreService
 * @param confidentialStoreService @see ConfidentialStoreService
 */
@Injectable()
export class CommonLibService extends SupportClass {
  constructor(
    public alertMessageStoreService: AlertMessageStoreService,
    public apiErrorResponseService: ApiErrorResponseService,
    public aswContextStoreService: AswContextStoreService,
    public aswCommonStoreService: AswCommonStoreService,
    public aswServiceStoreService: AswServiceStoreService,
    public amcMemberStoreService: AMCMemberStoreService,
    public anaBizContextStoreService: AnaBizContextStoreService,
    public dynamicContentService: DynamicContentService,
    public errorsHandlerService: ErrorsHandlerService,
    public notificationStoreService: NotificationStoreService,
    public aswMasterService: AswMasterService,
    public confidentialStoreService: ConfidentialStoreService,
    public loggerDatadogService: LoggerDatadogService
  ) {
    super();
    this.subscribeService(
      'CommonLibService_getApiErrorResponse',
      this.apiErrorResponseService.getApiErrorResponse$(),
      (state) => {
        this.apiError = state;
      }
    );
  }

  destroy() {}

  //　判定したいときobservableでは使えないのでcommonにデータを保持しておく
  public apiError: ApiErrorResponseModel | null = null;

  /**
   * 未ログインチェック
   * @returns ログインステータスがNOT_LOGINであればtrue
   */
  isNotLogin() {
    return this.aswContextStoreService.aswContextData.loginStatus === LoginStatusType.NOT_LOGIN;
  }

  /**
   * 日本オフィスチェック
   * @returns 日本オフィスであればtrue　統合オフィスコードが日本であるかを判定
   *
   * 参考
   * 有償日本オフィス "TYONH08AA";
   * 有償日本オフィスAPF "TYONH08AP";
   * 特典日本オフィス "TYONH08ZZ";
   * 日本統合オフィスコード "TYONH08";
   *
   */
  isJapaneseOffice() {
    return this.aswContextStoreService.aswContextData.pointOfSaleId.startsWith('TYONH08');
  }

  /**
   * APFオフィスチェック
   * @returns APFであればtrue
   *
   * 参考
   * 有償APFオフィス "TYONH08AP"
   */
  isApfOffice() {
    return this.aswContextStoreService.aswContextData.pointOfSaleId === 'TYONH08AP';
  }

  /**
   * セッションストレージ情報操作 読み取り
   * @param sessionName セッションストレージ名
   * @param isJson Jsonデータの場合true
   * @returns セッションストレージの値　存在しない場合null
   */
  public loadSessionStorage(sessionName: string, isJson = false): any {
    const session = sessionStorage.getItem(sessionName);
    if (session) {
      if (isJson) {
        return JSON.parse(session); // errorにしてあげたほうが良いのでtry catchしない
      } else {
        return session;
      }
    }
    return null;
  }

  /**
   * セッションストレージ情報操作 書き込み
   * @param item 保存するデータ
   * @param sessionName セッションストレージ名
   * @param isJson Jsonデータの場合true
   */
  public saveSessionStorage(item: any, sessionName: string, isJson = false): void {
    sessionStorage.setItem(sessionName, isJson ? JSON.stringify(item) : item);
  }
}
